import React, { useEffect, useState } from "react";
import { Button } from "@/components/Button/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AdoptionConnection,
  AttributionSource,
} from "../../api/getAdoptionConnections";
import { UpdateConnectionAttributionRequest } from "../../api/updateConnectionAttribution";

interface AttributionDialogProps {
  connection: AdoptionConnection | null;
  saving: boolean;
  onClose: () => void;
  onSave: (request: UpdateConnectionAttributionRequest) => void;
}

const AttributionDialog: React.FC<AttributionDialogProps> = ({
  connection,
  saving,
  onClose,
  onSave,
}) => {
  const [attributionSource, setAttributionSource] =
    useState<AttributionSource>("self");
  const [coachName, setCoachName] = useState("");

  useEffect(() => {
    if (connection) {
      setAttributionSource(connection.attributionSource ?? "self");
      setCoachName(connection.coachName ?? "");
    }
  }, [connection]);

  const isCoach = attributionSource === "coach";
  const canSave = !saving && (!isCoach || coachName.trim().length > 0);

  const handleSave = () => {
    onSave({
      attributionSource,
      coachName: isCoach ? coachName.trim() : null,
    });
  };

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={!!connection}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-normal">Edit Attribution</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500 dark:text-gray-400">
          How did {connection?.companyName ?? "this company"} come to connect
          the Data Connector?
        </p>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label
              className="text-gray-900 dark:text-white"
              htmlFor="attribution-source"
            >
              Attribution
            </Label>
            <Select
              onValueChange={(value) =>
                setAttributionSource(value as AttributionSource)
              }
              value={attributionSource}
            >
              <SelectTrigger id="attribution-source">
                <SelectValue placeholder="Select attribution" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="self">Self-connected</SelectItem>
                <SelectItem value="coach">Coach-encouraged</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isCoach && (
            <div className="space-y-1.5">
              <Label
                className="text-gray-900 dark:text-white"
                htmlFor="coach-name"
              >
                Coach name
              </Label>
              <Input
                id="coach-name"
                onChange={(event) => setCoachName(event.target.value)}
                placeholder="Coach name"
                value={coachName}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button disabled={saving} onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button disabled={!canSave} onClick={handleSave}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttributionDialog;
